import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import type { RacebandGridChannel, VideoProtocol } from "@/lib/rf-grid";
import { decideRsvpPlacement, needsPaymentBeforeSeat } from "@/lib/rsvp-placement";
import { assertHostsEvent } from "@/lib/host.functions";

/**
 * SRD 02 — RSVP.
 *
 * squadflight.com is the master of record: Partiful / Facebook / Meetup rows
 * are imported into the same table, never the other way round.
 *
 * Rules baked in here:
 *  - Native RSVP requires an authenticated pilot. Name/email stubs only come
 *    from the import path, so the public form can never mint a pilot record
 *    and skip the SRD 0 age gate.
 *  - Guardians RSVP for their minors; minors are created by a guardian only.
 *  - Guests are spectators. They consume room capacity, never a channel.
 *  - Only events with `requires_freq_allocation` touch the frequency engine.
 */

export const VTX_PREFERENCES = [
  "ANALOG_5G8",
  "DIGITAL_HDZERO",
  "DIGITAL_WALKSNAIL",
  "DIGITAL_DJI",
  "FLY_AND_SWAP",
] as const;

export const RSVP_STATUSES = [
  "invited",
  "going",
  "declined",
  "maybe",
  "waitlisted",
  "cancelled",
  "no_show",
  "attended",
] as const;
export type RsvpStatus = (typeof RSVP_STATUSES)[number];

export const FREQ_STATUSES = ["not_required", "pending", "seated", "blocked_hard_link"] as const;
export type FreqStatus = (typeof FREQ_STATUSES)[number];

export const RSVP_SOURCES = [
  "native",
  "partiful",
  "facebook",
  "meetup",
  "admin",
  "walk_in",
] as const;

const RSVP_COLS =
  "id, event_id, pilot_id, status, vtx_preference, freq_status, guest_count, note, source, external_id, external_name, responded_at, checked_in_at, created_at, wants_kit, kit_status, payment_state, payment_marked_at, photo_consent, guest_email, payment_nudge_stage, payment_nudge_at, nudges_paused_at, attend_role, attend_interest";

export type Rsvp = {
  id: string;
  /** "pilot" flies with a seat and channel; "spectator" attends free without one. */
  attend_role?: "pilot" | "spectator" | null;
  /** Answer to "What's your interest in flying at this event?" */
  attend_interest?: string | null;
  event_id: string;
  pilot_id: string | null;
  status: RsvpStatus;
  vtx_preference: VideoProtocol | null;
  freq_status: FreqStatus;
  guest_count: number;
  note: string | null;
  source: string;
  external_id: string | null;
  external_name: string | null;
  responded_at: string;
  checked_in_at: string | null;
  created_at: string;
  wants_kit?: boolean;
  kit_status?: KitStatus;
  payment_state?: PaymentState;
  payment_marked_at?: string | null;
  photo_consent?: boolean;
  /** Email supplied with the RSVP. */
  guest_email?: string | null;
  /** Automatic payment follow-up: which touch has gone out, and whether it's paused. */
  payment_nudge_stage?: string;
  payment_nudge_at?: string | null;
  nudges_paused_at?: string | null;
};

export const PAYMENT_STATES = [
  "unpaid",
  /** They told us they paid through an external link; crew hasn't confirmed it. */
  "pending_review",
  "paid",
  "refunded",
  "credited",
] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

export const KIT_STATUSES = ["none", "reserved", "handed_over"] as const;
export type KitStatus = (typeof KIT_STATUSES)[number];

export type RsvpEvent = {
  id: string;
  slug: string;
  title: string;
  /** One-line subheadline shown under the title. */
  subtitle?: string | null;
  description: string | null;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  cover_image_url: string | null;
  partiful_url: string | null;
  status: string;
  /** Pilot spots. Spectators are counted separately. */
  capacity: number;
  spectator_capacity: number;
  visibility: string;
  requires_freq_allocation: boolean;
  rsvp_opens_at: string | null;
  rsvp_closes_at: string | null;
  host_kind: "squadflight" | "community" | null;
  /** Paid seats: 0 means free. */
  price_cents?: number;
  kit_price_cents?: number;
  kit_label?: string | null;
  /** Staff-configured Stripe Payment Link for the seat only. */
  payment_link_url?: string | null;
  /** Staff-configured Stripe Payment Link for seat + kit bundle. */
  payment_link_with_kit_url?: string | null;
  kit_only_payment_link_url?: string | null;
  photo_consent_required?: boolean;
  /** Free Proving Ground intro session — accepts a /start pass. */
  is_intro_session?: boolean;
  /** Discount-code kinds this session takes, e.g. `lvl0_upgrade`. */
  accepted_pass_kinds?: string[] | null;
  /** Reduced-price Payment Link, used when a discount code was applied. */
  discount_payment_link_url?: string | null;
  /** Training, Track Session, Freestyle Session, Meetup, League Session. */
  event_type?: string;
  /** `internal` = RSVP here; anything else means we link out. */
  rsvp_mode?: string | null;
  facebook_event_url?: string | null;
  meetup_event_url?: string | null;
  multigp_event_url?: string | null;
  /** True when crew saved a private street address — never the address itself. */
  has_private_address?: boolean;
};

const PUBLIC_EVENT_COLS =
  "id, slug, title, subtitle, description, location, starts_at, ends_at, cover_image_url, partiful_url, status, capacity, spectator_capacity, visibility, requires_freq_allocation, rsvp_opens_at, rsvp_closes_at, host_kind, price_cents, kit_price_cents, kit_label, payment_link_url, payment_link_with_kit_url, kit_only_payment_link_url, photo_consent_required, is_intro_session, accepted_pass_kinds, discount_payment_link_url, event_type, rsvp_mode, facebook_event_url, meetup_event_url, multigp_event_url";

function publicClient() {
  const url = process.env["SUPABASE_URL"];
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"];
  if (!url || !key) return null;
  return createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input: RequestInfo | URL, init?: RequestInit) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export type RsvpWindow =
  { open: true } | { open: false; reason: "not_open_yet" | "closed" | "cancelled" | "past" };

export function rsvpWindowFor(event: RsvpEvent, now = new Date()): RsvpWindow {
  if (event.status === "cancelled") return { open: false, reason: "cancelled" };
  if (
    event.status === "ended" ||
    new Date(event.starts_at).getTime() < now.getTime() - 6 * 3600_000
  ) {
    return { open: false, reason: "past" };
  }
  if (event.rsvp_opens_at && new Date(event.rsvp_opens_at) > now) {
    return { open: false, reason: "not_open_yet" };
  }
  if (event.rsvp_closes_at && new Date(event.rsvp_closes_at) < now) {
    return { open: false, reason: "closed" };
  }
  return { open: true };
}

/* ------------------------------------------------------------------ *
 * Public read
 * ------------------------------------------------------------------ */

export type PublicEventHost = { display_name: string; is_owner: boolean };

export const getPublicEvent = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(120) }).parse(data),
  )
  .handler(async ({ data }) => {
    const db = publicClient();
    if (!db)
      return { event: null as RsvpEvent | null, counts: null, hosts: [] as PublicEventHost[] };

    // Slug-keyed read: drafts are reachable by their exact link (unlisted),
    // but never appear in any listing. See public.event_by_slug.
    const { data: rows, error } = await db.rpc("event_by_slug", { _slug: data.slug } as never);
    const row = (Array.isArray(rows) ? rows[0] : rows) as
      Record<string, unknown> | null | undefined;
    if (error || !row)
      return { event: null as RsvpEvent | null, counts: null, hosts: [] as PublicEventHost[] };

    const event = row as unknown as RsvpEvent;
    const { data: counts } = await db.rpc("event_rsvp_counts", { _event_id: event.id } as never);
    const c = (Array.isArray(counts) ? counts[0] : counts) as
      { going: number; waitlisted: number; guests: number } | null | undefined;

    const { data: hostRows } = await db.rpc("event_hosts_public", { _slug: data.slug } as never);

    return {
      event,
      counts: {
        going: Number(c?.going ?? 0),
        waitlisted: Number(c?.waitlisted ?? 0),
        guests: Number(c?.guests ?? 0),
      },
      hosts: ((hostRows ?? []) as PublicEventHost[]).filter((h) => h.display_name?.trim()),
    };
  });

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

async function adminDb() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

/** The pilot record behind the signed-in user, if any. */
async function myPilotId(context: { supabase: any; userId: string }): Promise<string | null> {
  const { data } = await context.supabase.rpc("pilot_id_for_user", { _user_id: context.userId });
  return (data as string | null) ?? null;
}

/** Self, a minor you are guardian of, or crew/admin. */
async function assertCanActFor(context: { supabase: any; userId: string }, pilotId: string) {
  const { data, error } = await context.supabase.rpc("can_act_for_pilot", { _pilot_id: pilotId });
  if (error) throw new Error("Could not verify who you are RSVPing for.");
  if (!data) throw new Error("You cannot RSVP for that pilot.");
}

async function assertCrew(context: { supabase: any; userId: string }) {
  const { resolveViewerTier } = await import("@/lib/pilot-profile.functions");
  const tier = await resolveViewerTier(context as never);
  if (tier !== "crew" && tier !== "admin") throw new Error("Unauthorized");
  return tier;
}

async function loadEvent(
  pilotFacing: any,
  eventId: string,
): Promise<RsvpEvent & { tenant_id: string }> {
  const { data } = await pilotFacing
    .from("events")
    .select(`${PUBLIC_EVENT_COLS}, tenant_id, freq_escalation_hours`)
    .eq("id", eventId)
    .maybeSingle();
  if (!data) throw new Error("Could not find that event.");
  return data as RsvpEvent & { tenant_id: string };
}

async function seatedCountFor(eventId: string): Promise<{ going: number; guests: number }> {
  const db = await adminDb();
  const { data } = await db
    .from("event_rsvps")
    .select("guest_count, attend_role")
    .eq("event_id", eventId)
    .eq("status", "going");
  const rows = (data ?? []) as { guest_count: number; attend_role: string | null }[];
  const spectators = rows.filter((r) => r.attend_role === "spectator").length;
  return {
    going: rows.length - spectators,
    guests: spectators + rows.reduce((sum, r) => sum + (r.guest_count ?? 0), 0),
  };
}

/* ------------------------------------------------------------------ *
 * Pilot-facing RSVP
 * ------------------------------------------------------------------ */

export type MyRsvpProfile = {
  default_video_protocol: VideoProtocol | null;
  preferred_raceband: RacebandGridChannel;
  fallback_raceband: RacebandGridChannel;
};

export const getMyRsvp = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const pilotId = await myPilotId(context as never);
    if (!pilotId) {
      return {
        rsvp: null as Rsvp | null,
        pilotId: null,
        party: [] as Rsvp[],
        profile: null as MyRsvpProfile | null,
        venueAddress: null as string | null,
        mapUrl: null as string | null,
        addressReleased: false,
      };
    }

    const { data: rows } = await (context as any).supabase
      .from("event_rsvps")
      .select(RSVP_COLS)
      .eq("event_id", data.eventId);

    // The RSVP form pre-fills from the pilot's own gear defaults.
    const { data: profileRow } = await (context as any).supabase
      .from("pilots")
      .select("default_video_protocol, preferred_raceband, fallback_raceband")
      .eq("id", pilotId)
      .maybeSingle();

    const all = (rows ?? []) as unknown as Rsvp[];
    const mine = all.find((r) => r.pilot_id === pilotId) ?? null;
    let venueAddress: string | null = null;
    let mapUrl: string | null = null;
    let addressReleased = false;
    const { data: eventRow } = await (context as any).supabase
      .from("events")
      .select("starts_at")
      .eq("id", data.eventId)
      .maybeSingle();
    if (eventRow?.starts_at) {
      const { mapUrlFor, releaseAtFor, venueAddressMayBeShown } = await import("@/lib/arrival");
      addressReleased = Date.now() >= new Date(releaseAtFor(eventRow.starts_at)).getTime();
      if (addressReleased && venueAddressMayBeShown(mine?.status)) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: privateRow } = await supabaseAdmin
          .from("event_private_details")
          .select("venue_address")
          .eq("event_id", data.eventId)
          .maybeSingle();
        venueAddress = privateRow?.venue_address?.trim() || null;
        mapUrl = venueAddress ? (mapUrlFor(venueAddress) ?? null) : null;
      }
    }
    return {
      pilotId,
      rsvp: mine,
      // Minors and household members this guardian can see through RLS.
      party: all.filter((r) => r.pilot_id !== pilotId),
      profile: (profileRow ?? null) as MyRsvpProfile | null,
      venueAddress,
      mapUrl,
      addressReleased,
    };
  });

/**
 * Lightweight signed-in calendar state. Public event lists stay cacheable,
 * while this separate protected read lets each pilot see their own RSVP.
 */
export const getMyEventRsvpStatuses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const pilotId = await myPilotId(context as never);
    if (!pilotId) return {} as Record<string, RsvpStatus>;

    const { data: rows, error } = await context.supabase
      .from("event_rsvps")
      .select("event_id, status")
      .eq("pilot_id", pilotId);

    if (error) throw new Error("Could not load your RSVP status.");

    return Object.fromEntries(
      (rows ?? []).map((row) => [row.event_id, row.status as RsvpStatus]),
    ) as Record<string, RsvpStatus>;
  });

export const submitRsvp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        /** Omit to RSVP yourself; pass a minor's id to RSVP as their guardian. */
        pilotId: z.string().uuid().optional(),
        vtxPreference: z.enum(VTX_PREFERENCES).nullable().optional(),
        guestCount: z.number().int().min(0).max(6).default(0),
        note: z.string().trim().max(500).nullable().optional(),
        /** Paid events: add the hardware kit to their order. */
        wantsKit: z.boolean().optional(),
        /** Photo/video consent, asked only when the event requires it. */
        photoConsent: z.boolean().optional(),
        /** Proving Ground free pass code from /start. */
        passCode: z.string().trim().max(32).optional(),
        /** Explicit answer from the pilot-facing RSVP panel. */
        status: z.enum(["going", "maybe", "cancelled"]).optional(),
        /** Flying (pilot seat) or spectating (free, no channel). */
        role: z.enum(["pilot", "spectator"]).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const selfPilotId = await myPilotId(ctx);
    const pilotId = data.pilotId ?? selfPilotId;
    if (!pilotId) {
      throw new Error("Finish setting up your pilot record before you RSVP.");
    }
    if (pilotId !== selfPilotId) await assertCanActFor(ctx, pilotId);

    const event = await loadEvent(ctx.supabase, data.eventId);
    const window = rsvpWindowFor(event);
    if (!window.open) {
      throw new Error(
        window.reason === "not_open_yet"
          ? "RSVPs for this event have not opened yet."
          : window.reason === "cancelled"
            ? "This event was cancelled."
            : "RSVPs for this event are closed.",
      );
    }

    const db = await adminDb();

    // A Proving Ground pass or discount code is checked before anything is
    // written, so a bad code never leaves half an RSVP behind.
    let passId: string | null = null;
    let passDiscountCents = 0;
    if (data.passCode && data.passCode.trim()) {
      const { checkIntroPass, passMessage } = await import("@/lib/intro-pass.server");
      const check = await checkIntroPass(db, {
        code: data.passCode,
        pilotId,
        eventIsIntroSession: Boolean((event as { is_intro_session?: boolean }).is_intro_session),
        acceptedKinds:
          (event as { accepted_pass_kinds?: string[] | null }).accepted_pass_kinds ?? null,
      });
      if (!check.ok) throw new Error(passMessage(check.reason));
      passId = check.passId;
      passDiscountCents = check.discountCents;
    }

    const { data: existing } = await db
      .from("event_rsvps")
      .select(RSVP_COLS)
      .eq("event_id", data.eventId)
      .eq("pilot_id", pilotId)
      .maybeSingle();
    const prior = existing as unknown as Rsvp | null;
    const role: "pilot" | "spectator" =
      data.role ??
      ((prior as { attend_role?: string } | null)?.attend_role === "spectator"
        ? "spectator"
        : "pilot");
    const spectator = role === "spectator";

    const requestedStatus = data.status;
    let status: RsvpStatus;
    let guestCount = data.guestCount;

    if (requestedStatus === "cancelled") {
      // Re-use the cancellation path so seats and waitlist behave the same.
      if (!prior || !pilotId) {
        return { rsvp: null, status: "cancelled" as RsvpStatus, seat: null };
      }
      const { cancelRsvpById } = await import("@/lib/rsvp-cancel.server");
      await cancelRsvpById(db, {
        tenantId: event.tenant_id,
        eventId: data.eventId,
        rsvpId: prior.id,
        pilotId,
        event,
      });
      return { rsvp: { ...prior, status: "cancelled" }, status: "cancelled", seat: null };
    }

    // Paid sessions: the money is the reservation. Filling in the form records
    // interest only — `invited` holds no seat, no capacity, no channel — and the
    // seat is placed by `seatRsvpAfterPayment` once the charge lands. A full
    // comp pass or an already-settled seat skips straight to placement.
    const priceCents = Math.max(0, Number(event.price_cents ?? 0));
    // A code with no amount comps the seat, and so does one worth the whole
    // ticket — either way there is nothing left to charge, so no card step.
    const fullComp =
      Boolean(passId) && (passDiscountCents === 0 || passDiscountCents >= priceCents);
    const alreadySettled = prior?.payment_state === "paid" || prior?.payment_state === "credited";
    const needsPaymentFirst = needsPaymentBeforeSeat({
      priceCents,
      fullComp,
      alreadySettled,
      requestedStatus,
      role,
    });

    if (requestedStatus === "maybe") {
      status = "maybe";
    } else if (needsPaymentFirst) {
      status = "invited";
    } else {
      const counts = await seatedCountFor(data.eventId);
      const placement = decideRsvpPlacement({
        going: counts.going,
        guests: counts.guests,
        capacity: event.capacity,
        spectatorCapacity: event.spectator_capacity ?? 0,
        requestedGuests: spectator ? 0 : data.guestCount,
        role,
        prior: prior
          ? {
              status: prior.status,
              guestCount: prior.guest_count ?? 0,
              role:
                (prior as { attend_role?: string }).attend_role === "spectator"
                  ? "spectator"
                  : "pilot",
            }
          : null,
      });
      status = placement.status;
      guestCount = placement.guestCount;
    }

    const { data: pilot } = await db
      .from("pilots")
      .select(
        "id, preferred_raceband, fallback_raceband, default_video_protocol, is_minor, guardian_pilot_id",
      )
      .eq("id", pilotId)
      .maybeSingle();
    if (!pilot) throw new Error("Could not find that pilot record.");
    const p = pilot as {
      preferred_raceband: RacebandGridChannel;
      fallback_raceband: RacebandGridChannel;
      default_video_protocol: VideoProtocol | null;
      is_minor: boolean;
      guardian_pilot_id: string | null;
    };
    if (p.is_minor && !p.guardian_pilot_id) {
      throw new Error("A minor pilot needs a guardian on file before they can RSVP.");
    }

    const vtx = (data.vtxPreference ?? p.default_video_protocol ?? null) as VideoProtocol | null;

    const row = {
      tenant_id: event.tenant_id,
      event_id: data.eventId,
      pilot_id: pilotId,
      status,
      vtx_preference: vtx,
      guest_count: spectator ? 0 : guestCount,
      note: data.note ?? null,
      source: "native",
      attend_role: role,
      freq_status:
        !spectator && event.requires_freq_allocation && status === "going"
          ? "pending"
          : "not_required",
      responded_at: new Date().toISOString(),
      ...(data.wantsKit !== undefined && (event.kit_price_cents ?? 0) > 0
        ? { wants_kit: data.wantsKit, kit_status: data.wantsKit ? "reserved" : "none" }
        : {}),
      ...(data.photoConsent !== undefined ? { photo_consent: data.photoConsent } : {}),
    };

    // The (event_id, pilot_id) unique index is partial (pilot_id IS NOT NULL),
    // so Postgres cannot match it for ON CONFLICT. Branch explicitly instead.
    let saved: unknown = null;
    let error: { code?: string } | null = null;

    if (prior) {
      const res = await db
        .from("event_rsvps")
        .update(row as never)
        .eq("id", prior.id)
        .select(RSVP_COLS)
        .single();
      saved = res.data;
      error = res.error;
    } else {
      const res = await db
        .from("event_rsvps")
        .insert(row as never)
        .select(RSVP_COLS)
        .single();
      saved = res.data;
      error = res.error;

      // Two submits landing at once: fall back to updating the row that won.
      if (error?.code === "23505") {
        const retry = await db
          .from("event_rsvps")
          .update(row as never)
          .eq("event_id", data.eventId)
          .eq("pilot_id", pilotId)
          .select(RSVP_COLS)
          .single();
        saved = retry.data;
        error = retry.error;
      }
    }

    if (error || !saved) {
      console.error("rsvp save failed", error);
      throw new Error("Could not save your RSVP.");
    }

    // The seat exists, so now spend the code: a full pass comps the seat, a
    // discount code just reduces what they owe.
    if (passId) {
      const pass = await import("@/lib/intro-pass.server");
      const claimed = await pass.claimIntroPass(db, {
        passId,
        eventId: data.eventId,
        redeemedBy: ctx.userId,
      });
      if (!claimed) throw new Error("That code has already been used.");

      // The ledger always records what the code was worth.
      if (passDiscountCents > 0) {
        await db
          .from("event_rsvps")
          .update({ discount_cents: passDiscountCents, promo_code_id: passId } as never)
          .eq("id", (saved as { id: string }).id);
      }
      // Nothing left to charge: settle the seat outright, no card step.
      if (fullComp) {
        await markSeatComped(db, {
          tenantId: event.tenant_id,
          rsvpId: (saved as { id: string }).id,
          priorState: (saved as { payment_state?: string }).payment_state ?? null,
        });
        (saved as { payment_state?: string }).payment_state = "credited";
      }
    }

    // Attribution: an RSVP is a real signal of interest, new or repeat.
    if (!prior) {
      const { recordTouch } = await import("@/lib/touches.server");
      await recordTouch(db, {
        pilotId,
        touchType: "rsvp",
        note: `RSVP (${status}) for ${event.title ?? "an event"}`,
      });
    }

    let seat: Awaited<
      ReturnType<typeof import("@/lib/rsvp-seating.server").seatPilotForRsvp>
    > | null = null;

    if (spectator && prior && (prior as { attend_role?: string }).attend_role !== "spectator") {
      const seating = await import("@/lib/rsvp-seating.server");
      await seating.releaseSeatAndRescan({ tenantId: event.tenant_id, eventId: event.id, pilotId });
    }

    if (!spectator && event.requires_freq_allocation && status === "going") {
      const seating = await import("@/lib/rsvp-seating.server");
      const session = await seating.ensureSessionForEventServer(event);
      seat = await seating.seatPilotForRsvp({
        tenantId: event.tenant_id,
        sessionId: session.id,
        sessionDate: session.session_date,
        maxConcurrentPilots: session.max_concurrent_pilots,
        pilotId,
        preferred: p.preferred_raceband,
        fallback: p.fallback_raceband,
        protocol: vtx,
        awaitingGear: vtx === "FLY_AND_SWAP",
      });
      await db
        .from("event_rsvps")
        .update({
          freq_status:
            seat.kind === "seated"
              ? "seated"
              : seat.kind === "blocked_hard_link"
                ? "blocked_hard_link"
                : "pending",
        } as never)
        .eq("id", (saved as { id: string }).id);
    }

    // Tell them it's booked. Guests get the verify-your-email version from the
    // hold flow instead, so this only fires for signed-in pilots.
    try {
      const comms = await import("@/lib/event-comms.server");
      await comms.sendRsvpNotice({
        db,
        rsvpId: (saved as { id: string }).id,
        pilotId,
        event: event as never,
        seatLine: seat && seat.kind === "seated" ? seatLineFor(seat) : null,
        wantsKit: Boolean((row as { wants_kit?: boolean }).wants_kit),
        status,
        spectator,
      });
      if (seat && seat.kind === "seated") {
        await comms.sendSeatAssignment({
          db,
          pilotId,
          eventId: event.id,
          heatNo: seat.heatNo,
          station: seat.station,
          seatNo: seat.seatNo,
          channel: seat.channel,
        });
      }
    } catch (e) {
      console.error("rsvp notice email failed", e);
    }

    // Tell Loops straight away so a campaign drip can react (or stop).
    {
      const { trackLoopsRsvpEvent, loopsEventForStatus } =
        await import("@/lib/loops-events.server");
      const loopsEvent = loopsEventForStatus(status);
      if (loopsEvent) await trackLoopsRsvpEvent((saved as { id: string }).id, loopsEvent);
    }

    return {
      rsvp: {
        ...(saved as unknown as Rsvp),
        freq_status: spectator ? "not_required" : seatFreqStatus(seat, event),
      },
      status,
      seat,
    };
  });

/** A redeemed intro pass covers the seat: record it like any other payment. */
async function markSeatComped(
  db: any,
  opts: { tenantId: string; rsvpId: string; priorState: string | null },
): Promise<void> {
  await db
    .from("event_rsvps")
    .update({ payment_state: "credited", payment_marked_at: new Date().toISOString() } as never)
    .eq("id", opts.rsvpId);
  await db.from("rsvp_payment_log").insert({
    tenant_id: opts.tenantId,
    rsvp_id: opts.rsvpId,
    from_state: opts.priorState,
    to_state: "credited",
    note: "Proving Ground intro pass redeemed",
  } as never);
}

/** Human-readable "Heat 1 - Station C, chair 5 - R6" for emails. */
function seatLineFor(seat: {
  heatNo: number;
  station: string;
  seatNo: number;
  channel: string;
}): string {
  return `Heat ${seat.heatNo} · Station ${seat.station}, chair ${seat.seatNo} — ${seat.channel}.`;
}

function seatFreqStatus(
  seat: { kind: string } | null,
  event: { requires_freq_allocation: boolean },
): FreqStatus {
  if (!event.requires_freq_allocation || !seat)
    return event.requires_freq_allocation ? "pending" : "not_required";
  if (seat.kind === "seated") return "seated";
  if (seat.kind === "blocked_hard_link") return "blocked_hard_link";
  return "pending";
}

export const cancelRsvp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ eventId: z.string().uuid(), pilotId: z.string().uuid().optional() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const selfPilotId = await myPilotId(ctx);
    const pilotId = data.pilotId ?? selfPilotId;
    if (!pilotId) throw new Error("Nothing to cancel.");
    if (pilotId !== selfPilotId) await assertCanActFor(ctx, pilotId);

    const event = await loadEvent(ctx.supabase, data.eventId);
    const db = await adminDb();

    const { cancelRsvpById } = await import("@/lib/rsvp-cancel.server");
    const { promoted, waitlistPromoted } = await cancelRsvpById(db, {
      tenantId: event.tenant_id,
      eventId: data.eventId,
      rsvpId: undefined,
      pilotId,
      event,
    });

    return { ok: true, promoted, waitlistPromoted };
  });

/* ------------------------------------------------------------------ *
 * Crew / host / self RSVP management
 * ------------------------------------------------------------------ */

const EDITABLE_STATUSES = [
  "going",
  "waitlisted",
  "maybe",
  "declined",
  "cancelled",
  "no_show",
] as const;

async function authorizeRsvpEdit(
  ctx: { supabase: any; userId: string },
  rsvp: { pilot_id: string | null; event_id: string },
) {
  const selfPilotId = await myPilotId(ctx);
  const isSelf = Boolean(rsvp.pilot_id && rsvp.pilot_id === selfPilotId);
  if (isSelf) return { authorized: true as const, isSelf: true as const };

  // Crew can edit any RSVP; hosts can edit their own events.
  const tier = await assertCrew(ctx).catch(() => null);
  if (tier) return { authorized: true as const, isSelf: false as const };

  await assertHostsEvent(ctx, rsvp.event_id);
  return { authorized: true as const, isSelf: false as const };
}

/** Change an RSVP answer. Re-seats or waitlists when moving to going. */
export const setRsvpStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        rsvpId: z.string().uuid(),
        status: z.enum(EDITABLE_STATUSES),
        /** Optional switch between flying and spectating. */
        role: z.enum(["pilot", "spectator"]).optional(),
        /** Crew/host only: why the change was made. */
        changeNote: z.string().trim().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const db = await adminDb();

    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select(RSVP_COLS)
      .eq("id", data.rsvpId)
      .maybeSingle();
    if (!rsvpRow) throw new Error("Could not find that RSVP.");
    const rsvp = rsvpRow as Rsvp;

    const { isSelf } = await authorizeRsvpEdit(ctx, rsvp);

    const event = await loadEvent(ctx.supabase, rsvp.event_id);

    // Cancellation re-uses the shared path.
    if (data.status === "cancelled") {
      const { cancelRsvpById } = await import("@/lib/rsvp-cancel.server");
      await cancelRsvpById(db, {
        tenantId: event.tenant_id,
        eventId: event.id,
        rsvpId: rsvp.id,
        pilotId: rsvp.pilot_id ?? "",
        event,
      });
      return { ok: true, status: "cancelled" as const };
    }

    // Moving to going: run placement so a full night puts them on the waitlist.
    let newStatus: RsvpStatus = data.status as RsvpStatus;
    let guestCount = rsvp.guest_count ?? 0;
    const priorRole =
      (rsvp as { attend_role?: string }).attend_role === "spectator" ? "spectator" : "pilot";
    const role: "pilot" | "spectator" = data.role ?? priorRole;
    const spectator = role === "spectator";
    if (spectator) guestCount = 0;
    // A pilot can't hand themselves a paid seat: unpaid means interest only.
    // Crew and hosts keep the override, which is how a comp gets recorded.
    const unsettled = rsvp.payment_state !== "paid" && rsvp.payment_state !== "credited";
    if (
      data.status === "going" &&
      !spectator &&
      isSelf &&
      Math.max(0, Number(event.price_cents ?? 0)) > 0 &&
      unsettled
    ) {
      newStatus = "invited";
    } else if (data.status === "going") {
      const counts = await seatedCountFor(event.id);
      const placement = decideRsvpPlacement({
        going: counts.going,
        guests: counts.guests,
        capacity: event.capacity,
        spectatorCapacity: event.spectator_capacity ?? 0,
        requestedGuests: guestCount,
        role,
        prior: { status: rsvp.status, guestCount: rsvp.guest_count ?? 0, role: priorRole },
      });
      newStatus = placement.status;
      guestCount = placement.guestCount;
    }

    const update: Record<string, unknown> = {
      status: newStatus,
      guest_count: guestCount,
      attend_role: role,
      responded_at: new Date().toISOString(),
      ...(spectator ? { freq_status: "not_required" } : {}),
    };
    // crew_note is staff-authored context: never writable by the pilot themselves.
    if (data.changeNote && !isSelf) update["crew_note"] = data.changeNote;

    await db
      .from("event_rsvps")
      .update(update as never)
      .eq("id", rsvp.id);

    // Switching to spectating hands their channel back to the board.
    if (spectator && priorRole === "pilot" && rsvp.pilot_id) {
      const seating = await import("@/lib/rsvp-seating.server");
      await seating.releaseSeatAndRescan({
        tenantId: event.tenant_id,
        eventId: event.id,
        pilotId: rsvp.pilot_id,
      });
    }

    // Seat them if they just landed a spot.
    if (!spectator && newStatus === "going" && event.requires_freq_allocation && rsvp.pilot_id) {
      const { data: pilot } = await db
        .from("pilots")
        .select("preferred_raceband, fallback_raceband, default_video_protocol")
        .eq("id", rsvp.pilot_id)
        .maybeSingle();
      const p = pilot as {
        preferred_raceband: RacebandGridChannel;
        fallback_raceband: RacebandGridChannel;
        default_video_protocol: VideoProtocol | null;
      } | null;
      if (p) {
        const seating = await import("@/lib/rsvp-seating.server");
        const session = await seating.ensureSessionForEventServer(event);
        const seat = await seating.seatPilotForRsvp({
          tenantId: event.tenant_id,
          sessionId: session.id,
          sessionDate: session.session_date,
          maxConcurrentPilots: session.max_concurrent_pilots,
          pilotId: rsvp.pilot_id ?? "",
          preferred: p.preferred_raceband,
          fallback: p.fallback_raceband,
          protocol: rsvp.vtx_preference ?? p.default_video_protocol,
          awaitingGear: rsvp.vtx_preference === "FLY_AND_SWAP",
        });
        await db
          .from("event_rsvps")
          .update({
            freq_status:
              seat.kind === "seated"
                ? "seated"
                : seat.kind === "blocked_hard_link"
                  ? "blocked_hard_link"
                  : "pending",
          } as never)
          .eq("id", rsvp.id);
      }
    }

    // Send the matching RSVP outcome email.
    try {
      const comms = await import("@/lib/event-comms.server");
      await comms.sendRsvpNotice({
        db,
        rsvpId: rsvp.id,
        pilotId: rsvp.pilot_id ?? "",
        event: event as never,
        seatLine: null,
        wantsKit: Boolean(rsvp.wants_kit),
        status: newStatus,
        spectator,
        resendTag: `status-change-${Date.now()}`,
      });
    } catch (e) {
      console.error("status-change RSVP email failed", e);
    }

    {
      const { trackLoopsRsvpEvent, loopsEventForStatus } =
        await import("@/lib/loops-events.server");
      const loopsEvent = loopsEventForStatus(newStatus);
      if (loopsEvent) await trackLoopsRsvpEvent(rsvp.id, loopsEvent);
    }

    return { ok: true, status: newStatus };
  });

/** Edit RSVP details without changing status. */
export const updateRsvpDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        rsvpId: z.string().uuid(),
        guestCount: z.number().int().min(0).max(6).optional(),
        note: z.string().trim().max(500).nullable().optional(),
        vtxPreference: z.enum(VTX_PREFERENCES).nullable().optional(),
        wantsKit: z.boolean().optional(),
        photoConsent: z.boolean().optional(),
        /** Crew/host only. */
        changeNote: z.string().trim().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const db = await adminDb();

    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select(RSVP_COLS)
      .eq("id", data.rsvpId)
      .maybeSingle();
    if (!rsvpRow) throw new Error("Could not find that RSVP.");
    const rsvp = rsvpRow as Rsvp;

    const { isSelf } = await authorizeRsvpEdit(ctx, rsvp);

    const update: Record<string, unknown> = {};
    if (data.guestCount !== undefined) update["guest_count"] = data.guestCount;
    if (data.note !== undefined) update["note"] = data.note;
    if (data.vtxPreference !== undefined) update["vtx_preference"] = data.vtxPreference;
    if (data.wantsKit !== undefined) {
      update["wants_kit"] = data.wantsKit;
      update["kit_status"] = data.wantsKit ? "reserved" : "none";
    }
    if (data.photoConsent !== undefined) update["photo_consent"] = data.photoConsent;
    // crew_note is staff-authored context: never writable by the pilot themselves.
    if (data.changeNote && !isSelf) update["crew_note"] = data.changeNote;

    if (Object.keys(update).length === 0) return { ok: true, rsvp };

    await db
      .from("event_rsvps")
      .update(update as never)
      .eq("id", rsvp.id);

    return { ok: true, rsvp: { ...rsvp, ...update } as Rsvp };
  });

/* ------------------------------------------------------------------ *
 * Household flow — "I'm bringing my child"
 * ------------------------------------------------------------------ */

type HouseholdMember = { id: string; first_name: string; last_name: string; is_minor: boolean };

export const listMyDependents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as { supabase: any; userId: string };
    const pilotId = await myPilotId(ctx);
    if (!pilotId) return { dependents: [] as HouseholdMember[] };

    const { data } = await ctx.supabase
      .from("pilots")
      .select("id, first_name, last_name, is_minor")
      .or(`guardian_pilot_id.eq.${pilotId},created_by_guardian_id.eq.${pilotId}`)
      .is("archived_at", null)
      .order("first_name");
    return { dependents: (data ?? []) as HouseholdMember[] };
  });

/**
 * Add a household member — your child, or anyone flying under your household.
 * Minors can ONLY be created this way: the public form never mints a pilot
 * record, so the SRD 0 age gate always runs through a guardian.
 *
 * They never inherit the guardian's email. Pilot emails are unique per tenant,
 * so copying it collided with the guardian's own record.
 */
// Blank optional text should mean "not given", never a validation failure.
const optionalText = (max: number) =>
  z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
    z.string().trim().max(max).optional(),
  );

export const addHouseholdMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const schema = z
      .object({
        firstName: z.string().trim().min(1, "First name is required.").max(80),
        lastName: z.string().trim().min(1, "Last name is required.").max(80),
        isMinor: z.boolean().default(false),
        dateOfBirth: z.preprocess(
          (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
          z
            .string()
            .trim()
            .regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2014-08-19.")
            .optional(),
        ),
        email: z.preprocess(
          (v) => (typeof v === "string" ? (v.trim() === "" ? undefined : v.trim()) : v),
          z
            .string()
            .email("That email doesn't look right — you can also leave it blank.")
            .max(160)
            .optional(),
        ),
        phone: optionalText(40),
        emergencyContactName: optionalText(120),
        emergencyContactPhone: optionalText(40),
        preferredRaceband: z.enum(["R1", "R3", "R6", "R8"]).default("R1"),
        vtxPreference: z.enum(VTX_PREFERENCES).nullable().optional(),
      })
      .superRefine((v, ctx) => {
        if (v.isMinor && !v.dateOfBirth) {
          ctx.addIssue({
            code: "custom",
            path: ["dateOfBirth"],
            message: "A date of birth is required for anyone under 18.",
          });
        }
        if (v.isMinor && !v.emergencyContactName?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["emergencyContactName"],
            message: "An emergency contact is required for anyone under 18.",
          });
        }
      });

    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      // Never hand the raw validator output to the UI — it reads like a stack trace.
      throw new Error(parsed.error.issues[0]?.message ?? "Please check those details.");
    }
    return parsed.data;
  })

  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const guardianId = await myPilotId(ctx);
    if (!guardianId) throw new Error("Set up your own pilot record first.");

    const db = await adminDb();
    const { data: guardian } = await db
      .from("pilots")
      .select("id, tenant_id, email, phone, household_id, last_name")
      .eq("id", guardianId)
      .maybeSingle();
    if (!guardian) throw new Error("Set up your own pilot record first.");
    const g = guardian as {
      tenant_id: string;
      email: string | null;
      phone: string | null;
      household_id: string | null;
      last_name: string;
    };

    // Age of record wins over the checkbox when a date of birth is given.
    const isMinor = data.dateOfBirth ? yearsSince(data.dateOfBirth) < 18 : data.isMinor;
    if (isMinor && !data.dateOfBirth) {
      throw new Error("A date of birth is required for anyone under 18.");
    }

    // Everyone in a household shares one household record.
    let householdId = g.household_id;
    if (!householdId) {
      const { data: house } = await db
        .from("households")
        .insert({
          tenant_id: g.tenant_id,
          name: `${g.last_name || "Pilot"} household`,
          head_pilot_id: guardianId,
        } as never)
        .select("id")
        .single();
      householdId = (house as { id: string } | null)?.id ?? null;
      if (householdId) {
        await db
          .from("pilots")
          .update({ household_id: householdId } as never)
          .eq("id", guardianId);
      }
    }

    const { data: created, error } = await db
      .from("pilots")
      .insert({
        tenant_id: g.tenant_id,
        first_name: data.firstName,
        last_name: data.lastName,
        // No email of their own is fine — we reach them through the guardian.
        email: data.email ?? null,
        phone: data.phone ?? null,
        date_of_birth: data.dateOfBirth ?? null,
        is_minor: isMinor,
        guardian_pilot_id: isMinor ? guardianId : null,
        created_by_guardian_id: guardianId,
        self_registered: false,
        household_id: householdId,
        lead_source: "Household add",
        preferred_raceband: data.preferredRaceband,
        default_video_protocol: data.vtxPreference ?? null,
        emergency_contact_name: data.emergencyContactName ?? null,
        emergency_contact_phone: data.emergencyContactPhone ?? null,
        public_profile_enabled: false,
      } as never)
      .select("id, first_name, last_name, is_minor")
      .single();
    if (error || !created) {
      console.error("household member create failed", error);
      if (error?.code === "23505") {
        throw new Error(
          "That email already belongs to another pilot. Leave it blank or use a different one.",
        );
      }
      throw new Error("Could not add that household member.");
    }

    // Hard-link guardian and member so seating never splits them.
    const memberId = (created as { id: string }).id;
    await db.from("pilot_wingmen").insert([
      { tenant_id: g.tenant_id, pilot_id: guardianId, wingman_pilot_id: memberId },
      { tenant_id: g.tenant_id, pilot_id: memberId, wingman_pilot_id: guardianId },
    ] as never);

    return { pilot: created as HouseholdMember, isMinor };
  });

function yearsSince(dob: string): number {
  const then = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - then.getFullYear();
  const m = now.getMonth() - then.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < then.getDate())) age -= 1;
  return age;
}

/* ------------------------------------------------------------------ *
 * Marshal / admin
 * ------------------------------------------------------------------ */

export type AttendeeRow = Rsvp & {
  pilot: {
    id: string;
    first_name: string;
    last_name: string;
    callsign: string | null;
    pilot_no: number | null;
    is_minor: boolean;
    email: string | null;
    default_video_protocol: VideoProtocol | null;
    preferred_raceband: RacebandGridChannel;
  } | null;
};

export const listEventAttendees = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const { data: rows } = await ctx.supabase
      .from("event_rsvps")
      .select(RSVP_COLS)
      .eq("event_id", data.eventId)
      .order("created_at", { ascending: true });
    const rsvps = (rows ?? []) as unknown as Rsvp[];

    const ids = [...new Set(rsvps.map((r) => r.pilot_id).filter(Boolean))] as string[];
    const pilots = new Map<string, AttendeeRow["pilot"]>();
    if (ids.length) {
      const { data: pilotRows } = await ctx.supabase
        .from("pilots")
        .select(
          "id, first_name, last_name, callsign, pilot_no, is_minor, email, default_video_protocol, preferred_raceband",
        )
        .in("id", ids);
      for (const p of (pilotRows ?? []) as NonNullable<AttendeeRow["pilot"]>[]) pilots.set(p.id, p);
    }

    const { data: eventRow } = await ctx.supabase
      .from("events")
      .select(
        "id, title, slug, subtitle, description, location, starts_at, ends_at, capacity, price_cents, kit_price_cents, kit_label, payment_link_url, payment_link_with_kit_url, event_type, cover_image_url, is_intro_session, host_kind",
      )
      .eq("id", data.eventId)
      .maybeSingle();

    return {
      attendees: rsvps.map((r) => ({
        ...r,
        pilot: r.pilot_id ? (pilots.get(r.pilot_id) ?? null) : null,
      })) as AttendeeRow[],
      event: (eventRow ?? null) as {
        id: string;
        title: string;
        slug: string;
        subtitle: string | null;
        description: string | null;
        location: string | null;
        starts_at: string;
        ends_at: string | null;
        capacity: number;
        price_cents: number;
        kit_price_cents: number;
        kit_label: string | null;
        payment_link_url: string | null;
        payment_link_with_kit_url: string | null;
        event_type: string | null;
        cover_image_url: string | null;
        is_intro_session: boolean | null;
        host_kind: "squadflight" | "community" | null;
      } | null,
    };
  });

export const checkInRsvp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ rsvpId: z.string().uuid(), undo: z.boolean().default(false) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { data: rsvp } = await db
      .from("event_rsvps")
      .select("id, event_id, pilot_id, status, checked_in_at, pre_checkin_status")
      .eq("id", data.rsvpId)
      .maybeSingle();
    if (!rsvp) throw new Error("Could not find that RSVP.");
    const r = rsvp as {
      id: string;
      event_id: string;
      pilot_id: string | null;
      status: string;
      checked_in_at: string | null;
      pre_checkin_status: string | null;
    };

    if (data.undo) {
      // Put them back on the list they were on before the door — someone
      // promoted off the waitlist should not silently become a booked seat.
      const back = r.pre_checkin_status ?? "going";
      await db
        .from("event_rsvps")
        .update({
          checked_in_at: null,
          checked_in_by: null,
          status: back,
          pre_checkin_status: null,
        } as never)
        .eq("id", r.id);
      return { ok: true, checkedIn: false, status: back };
    }

    const alreadyIn = !!r.checked_in_at;
    await db
      .from("event_rsvps")
      .update({
        checked_in_at: new Date().toISOString(),
        checked_in_by: ctx.userId,
        status: "attended",
        ...(alreadyIn ? {} : { pre_checkin_status: r.status }),
      } as never)
      .eq("id", r.id);

    // Economy: attendance pays once per event, and whoever invited this pilot
    // gets paid the first time their friend actually shows up.
    if (!alreadyIn && r.pilot_id) {
      const { emitEconomyEvent } = await import("@/lib/economy.server");
      await emitEconomyEvent({
        kind: "event_attendance",
        pilotId: r.pilot_id,
        eventId: r.event_id,
      });
      const { creditInviteAttendance } = await import("@/lib/invites.server");
      await creditInviteAttendance(r.pilot_id, r.event_id);

      // A lead who walks through the door is a real pilot — this promotion is
      // what mints their pilot number.
      const { promoteLeadToPilot } = await import("@/lib/leads.server");
      await promoteLeadToPilot(db, r.pilot_id, {
        trigger: "checkin",
        actorUserId: ctx.userId,
      });

      const { recordTouch } = await import("@/lib/touches.server");
      await recordTouch(db, {
        pilotId: r.pilot_id,
        eventId: r.event_id,
        touchType: "checkin",
        note: "Checked in at an event",
      });
    }

    {
      const { trackLoopsRsvpEvent } = await import("@/lib/loops-events.server");
      await trackLoopsRsvpEvent(r.id, "rsvp_checked_in");
    }

    return { ok: true, checkedIn: true };
  });

/** Marshal promotes someone off the waitlist (strict chronological by default). */
export const promoteFromWaitlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ rsvpId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { data: rsvp } = await db
      .from("event_rsvps")
      .select("id, event_id, pilot_id, vtx_preference, tenant_id")
      .eq("id", data.rsvpId)
      .maybeSingle();
    if (!rsvp) throw new Error("Could not find that RSVP.");
    const r = rsvp as {
      id: string;
      event_id: string;
      pilot_id: string | null;
      vtx_preference: VideoProtocol | null;
      tenant_id: string;
    };

    const event = await loadEvent(ctx.supabase, r.event_id);
    await db
      .from("event_rsvps")
      .update({ status: "going" } as never)
      .eq("id", r.id);

    let seated = false;
    if (event.requires_freq_allocation && r.pilot_id) {
      const { data: pilot } = await db
        .from("pilots")
        .select("preferred_raceband, fallback_raceband, default_video_protocol")
        .eq("id", r.pilot_id)
        .maybeSingle();
      const p = pilot as {
        preferred_raceband: RacebandGridChannel;
        fallback_raceband: RacebandGridChannel;
        default_video_protocol: VideoProtocol | null;
      } | null;
      if (p) {
        const seating = await import("@/lib/rsvp-seating.server");
        const session = await seating.ensureSessionForEventServer(event);
        const result = await seating.seatPilotForRsvp({
          tenantId: event.tenant_id,
          sessionId: session.id,
          sessionDate: session.session_date,
          maxConcurrentPilots: session.max_concurrent_pilots,
          pilotId: r.pilot_id,
          preferred: p.preferred_raceband,
          fallback: p.fallback_raceband,
          protocol: r.vtx_preference ?? p.default_video_protocol ?? null,
          awaitingGear: (r.vtx_preference ?? p.default_video_protocol) === "FLY_AND_SWAP",
        });
        seated = result.kind === "seated";
        await db
          .from("event_rsvps")
          .update({ freq_status: seated ? "seated" : "pending" } as never)
          .eq("id", r.id);

        const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
        const { data: contact } = await db
          .from("pilots")
          .select("first_name, email, email_opted_out")
          .eq("id", r.pilot_id)
          .maybeSingle();
        const c = contact as { first_name: string; email: string; email_opted_out: boolean } | null;
        if (c?.email && !c.email_opted_out) {
          await sendTemplateEmail("waitlist-promoted", c.email, {
            templateData: {
              firstName: c.first_name,
              eventTitle: event.title,
              startsAt: event.starts_at,
              location: event.location ?? "SquadFlight Arena",
              seated,
            },
          }).catch((err) => console.error("promotion email failed", err));
        }
      }
    }

    return { ok: true, seated };
  });

/**
 * Pilots still without a channel close to the event. The marshal sees these
 * before the doors open rather than discovering them on the line.
 */
export const listFreqEscalations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const { data: events } = await ctx.supabase
      .from("events")
      .select("id, title, slug, starts_at, freq_escalation_hours")
      .eq("requires_freq_allocation", true)
      .gte("starts_at", new Date().toISOString())
      .order("starts_at", { ascending: true })
      .limit(20);

    const rows: { eventId: string; eventTitle: string; startsAt: string; pending: number }[] = [];
    for (const ev of (events ?? []) as {
      id: string;
      title: string;
      starts_at: string;
      freq_escalation_hours: number;
    }[]) {
      const hoursOut = (new Date(ev.starts_at).getTime() - Date.now()) / 3600_000;
      if (hoursOut > (ev.freq_escalation_hours ?? 24)) continue;

      const { data: pending } = await ctx.supabase
        .from("event_rsvps")
        .select("id")
        .eq("event_id", ev.id)
        .eq("status", "going")
        .in("freq_status", ["pending", "blocked_hard_link"]);
      const count = (pending ?? []).length;
      if (count > 0) {
        rows.push({ eventId: ev.id, eventTitle: ev.title, startsAt: ev.starts_at, pending: count });
      }
    }
    return { escalations: rows };
  });

/* ------------------------------------------------------------------ *
 * Paid seats: money, kits and seat expansion
 * ------------------------------------------------------------------ */

/**
 * Marshal/admin marks a seat paid, refunded or credited. Paying is what turns
 * a lead into a numbered pilot, exactly as a card payment would.
 */
export const markRsvpPayment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        rsvpId: z.string().uuid(),
        state: z.enum(PAYMENT_STATES),
        note: z.string().trim().max(300).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { applyRsvpPayment } = await import("@/lib/rsvp-payment.server");
    const result = await applyRsvpPayment(db, {
      rsvpId: data.rsvpId,
      state: data.state,
      note: data.note ?? null,
      actorUserId: ctx.userId,
    });

    return { ok: true, promoted: result.promoted };
  });

/**
 * Pause or resume the automatic payment follow-up for one seat. A phone call
 * should never be contradicted by a robot email an hour later.
 */
export const setRsvpNudgesPaused = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ rsvpId: z.string().uuid(), paused: z.boolean() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { error } = await db
      .from("event_rsvps")
      .update({ nudges_paused_at: data.paused ? new Date().toISOString() : null } as never)
      .eq("id", data.rsvpId);
    if (error) throw new Error("Could not change the reminder setting.");
    return { ok: true, paused: data.paused };
  });

/**
 * Send the next payment follow-up now instead of waiting for the schedule.
 * Uses the same sequence logic, so the stage still advances by one.
 */
export const sendPaymentNudgeNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ rsvpId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { sendPaymentNudgeForRsvp } = await import("@/lib/payment-nudges.server");
    return sendPaymentNudgeForRsvp(db, data.rsvpId);
  });

/**
 * Send the address and arrival details for a night to everyone going, with each
 * pilot's station and channel from the frequency board.
 */
export const previewArrivalDetailsNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { previewArrivalDetails } = await import("@/lib/arrival-notice.server");
    return previewArrivalDetails(db, data.eventId);
  });

/** Crew hand the address over here; nothing is released by saving it. */
export const saveArrivalDetailsNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        venueAddress: z.string().max(400),
        arrivalNote: z.string().max(4000),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { saveArrivalDetails } = await import("@/lib/arrival-notice.server");
    return saveArrivalDetails(db, data.eventId, {
      venueAddress: data.venueAddress,
      arrivalNote: data.arrivalNote,
    });
  });

export const sendArrivalDetailsNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ eventId: z.string().uuid(), releaseEarly: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { sendArrivalDetails } = await import("@/lib/arrival-notice.server");
    return sendArrivalDetails(db, data.eventId, { releaseEarly: data.releaseEarly ?? false });
  });

/** Kit fulfilment: reserved when they ask for one, handed over on the night. */
export const setKitStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ rsvpId: z.string().uuid(), status: z.enum(KIT_STATUSES) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { error } = await db
      .from("event_rsvps")
      .update({
        kit_status: data.status,
        wants_kit: data.status !== "none",
      } as never)
      .eq("id", data.rsvpId);
    if (error) throw new Error("Could not update that kit.");
    return { ok: true };
  });

/**
 * Open more flying seats on a sold-out night and pull the waitlist in, oldest
 * first, so nobody has to be chased by hand.
 */
export const expandEventCapacity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ eventId: z.string().uuid(), capacity: z.number().int().min(1).max(500) })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const db = await adminDb();
    const { data: eventRow } = await db
      .from("events")
      .select("id, title, slug, capacity, starts_at, location, requires_freq_allocation")
      .eq("id", data.eventId)
      .maybeSingle();
    if (!eventRow) throw new Error("Could not find that event.");
    const ev = eventRow as {
      id: string;
      title: string;
      slug: string;
      capacity: number;
      starts_at: string;
      location: string | null;
      requires_freq_allocation: boolean;
      tenant_id?: string;
    };
    if (data.capacity <= ev.capacity) {
      throw new Error("That is not more seats than the event already has.");
    }

    const { error: capError } = await db
      .from("events")
      .update({ capacity: data.capacity } as never)
      .eq("id", ev.id);
    if (capError) throw new Error("Could not open more seats.");

    const event = await loadEvent(ctx.supabase, ev.id);
    const { fillFreedSeats } = await import("@/lib/waitlist-promote.server");
    const filled = await fillFreedSeats(
      db,
      {
        id: ev.id,
        tenant_id: event.tenant_id,
        title: ev.title,
        starts_at: ev.starts_at,
        location: ev.location ?? null,
        capacity: data.capacity,
        requires_freq_allocation: ev.requires_freq_allocation,
      },
      { reason: `seats-${data.capacity}` },
    );

    return { ok: true, capacity: data.capacity, promoted: filled.promoted };
  });

/** Everything the printable front-desk check-in sheet needs. Crew only; no contact details or street address. */
async function loadSheetFor(supabase: any, eventRow: any, waiversEnabled = false) {
  const { data: rows } = await supabase
    .from("event_rsvps")
    .select(
      "id, pilot_id, status, attend_role, guest_count, checked_in_at, payment_state, wants_kit, discount_cents, guest_name, external_name",
    )
    .eq("event_id", eventRow.id);
  const rsvps = (rows ?? []) as any[];
  const ids = [...new Set(rsvps.map((r) => r.pilot_id).filter(Boolean))] as string[];
  const pilots = new Map<string, any>();
  if (ids.length) {
    const { data: pr } = await supabase
      .from("pilots")
      .select(
        "id, first_name, last_name, callsign, pilot_no, is_minor, waiver_signed_at, safety_clearance_level, default_video_protocol",
      )
      .in("id", ids);
    for (const p of pr ?? []) pilots.set(p.id, p);
  }
  const { seatMapFor } = await import("@/lib/event-comms.server");
  const seats = await seatMapFor(supabase, eventRow);
  const { buildCheckinSheet } = await import("@/lib/checkin-sheet");
  return buildCheckinSheet(
    rsvps.map((r) => ({ ...r, pilot: r.pilot_id ? (pilots.get(r.pilot_id) ?? null) : null })),
    eventRow,
    seats,
    { waiversEnabled },
  );
}

function lvDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

export const getCheckinSheet = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);
    const COLS =
      "id, title, starts_at, location, price_cents, kit_price_cents, event_type, venue_id";
    const { data: eventRow } = await ctx.supabase
      .from("events")
      .select(COLS)
      .eq("id", data.eventId)
      .maybeSingle();
    if (!eventRow) throw new Error("Could not find that event.");
    const ev = eventRow as any;
    let venueName: string | null = null;
    if (ev.venue_id) {
      const { data: v } = await ctx.supabase
        .from("venues")
        .select("name")
        .eq("id", ev.venue_id)
        .maybeSingle();
      venueName = (v as any)?.name ?? null;
    }
    const isTraining = ev.event_type === "training";
    // Same Las Vegas day sibling (training <-> fun fly)
    const t = new Date(ev.starts_at).getTime();
    const { data: near } = await ctx.supabase
      .from("events")
      .select(COLS + ", status")
      .gte("starts_at", new Date(t - 20 * 3600e3).toISOString())
      .lte("starts_at", new Date(t + 20 * 3600e3).toISOString())
      .neq("id", ev.id);
    const sibling = ((near ?? []) as any[]).find(
      (e) =>
        lvDay(e.starts_at) === lvDay(ev.starts_at) &&
        e.status !== "cancelled" &&
        (e.event_type === "training") !== isTraining,
    );
    const { data: wv } = await ctx.supabase
      .from("site_config")
      .select("value")
      .eq("key", "waivers_enabled")
      .maybeSingle();
    const waiversOn = ((wv as any)?.value ?? "").trim().toLowerCase() === "true";
    let sheet = await loadSheetFor(ctx.supabase, ev, waiversOn);
    if (sibling) {
      const other = await loadSheetFor(ctx.supabase, sibling, waiversOn);
      const { mergeSameDaySheets } = await import("@/lib/checkin-sheet");
      sheet = isTraining ? mergeSameDaySheets(other, sheet) : mergeSameDaySheets(sheet, other);
    }
    const training = isTraining ? ev : (sibling ?? null);
    return {
      event: {
        title: ev.title as string,
        starts_at: ev.starts_at as string,
        place: (venueName ?? ev.location ?? null) as string | null,
      },
      training: training
        ? { title: training.title as string, starts_at: training.starts_at as string }
        : null,
      sheet,
    };
  });
